import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, ClipboardList, Apple, Activity, HeartPulse, Droplets, Bone, ShieldPlus,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { scrollToId, IMAGES } from "@/config";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import BookingModal from "@/components/BookingModal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const DIRECTION_ICONS = [ClipboardList, Apple, Activity, HeartPulse, Droplets, Bone, ShieldPlus];

function SectionHead({ eyebrow, title, id }) {
  return (
    <Reveal>
      <p className="text-xs font-mono uppercase tracking-[0.25em] text-terra mb-4">{eyebrow}</p>
      <h2 id={id} className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] tracking-tight leading-tight max-w-3xl text-balance">{title}</h2>
    </Reveal>
  );
}

function InlineCta({ label, onBook }) {
  return (
    <Reveal className="mt-10">
      <button onClick={onBook} data-testid="inline-booking-cta" className="group inline-flex items-center gap-2 rounded-full bg-sage px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-ink">
        {label} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
      </button>
    </Reveal>
  );
}

function Hero({ onBook }) {
  const { t } = useLanguage();
  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden grain pt-24 pb-16" data-testid="hero-section">
      <div className="absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full bg-terra-light blur-3xl opacity-60" />
      <div className="absolute -bottom-40 -left-40 w-[30rem] h-[30rem] rounded-full bg-sand blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center relative z-10 w-full">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-xs font-mono uppercase tracking-[0.25em] text-terra mb-6"
            data-testid="hero-slogan"
          >
            {t.hero.slogan}
          </motion.p>
          <h1 className="font-serif tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-balance" data-testid="hero-headline">
            {t.hero.title.split("—").map((part, i, arr) => (
              <span key={i} className="mask-line">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i > 0 && <span className="text-sage">— </span>}
                  {i === 1 ? <span className="text-sage">{part}</span> : part}
                  {i < arr.length - 1 && " "}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.75, duration: 0.7 }} className="mt-6 text-sm font-mono uppercase tracking-[0.14em] text-sage" data-testid="hero-sub">
            {t.hero.sub}
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.7 }} className="mt-5 text-base sm:text-lg text-muted2 leading-relaxed max-w-xl" data-testid="hero-extra">
            {t.hero.extra}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.6 }} className="mt-9 flex flex-wrap items-center gap-4">
            <button onClick={onBook} data-testid="booking-cta-hero" className="group inline-flex items-center gap-2 rounded-full bg-sage px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-ink hover:shadow-xl hover:-translate-y-0.5">
              {t.hero.cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={() => scrollToId("process")} data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-sm font-semibold transition-colors hover:border-sage hover:text-sage">
              {t.hero.cta2}
            </button>
          </motion.div>
        </div>
        <div className="lg:col-span-5 hidden lg:block">
          <div className="relative">
            <motion.div initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }} transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}>
              <img src={IMAGES.portrait} alt="Tatiana Nakryzhskaya, MD, PhD, CFMP" className="rounded-3xl object-cover w-full h-[560px] shadow-2xl" data-testid="hero-doctor-image" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl px-5 py-4 border border-line" data-testid="hero-name-badge">
              <p className="font-serif text-lg font-semibold">Tatiana Nakryzhskaya</p>
              <p className="text-xs font-mono uppercase tracking-widest text-terra">MD, PhD, CFMP</p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutTeaser({ onBook }) {
  const { lang, t } = useLanguage();
  return (
    <section className="py-20 lg:py-28" data-testid="about-teaser">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-5">
          <div className="relative max-w-sm mx-auto lg:mx-0">
            <img src={IMAGES.about} alt="Tatiana Nakryzhskaya, MD, PhD, CFMP" className="rounded-3xl object-cover w-full h-[420px] shadow-2xl" data-testid="about-teaser-photo" />
            <div className="absolute -bottom-5 -right-3 sm:-right-5 bg-white rounded-2xl shadow-xl px-5 py-3 border border-line">
              <p className="font-serif text-lg font-semibold leading-none">Tatiana Nakryzhskaya</p>
              <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-terra mt-1">MD, PhD, CFMP</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-terra mb-6">{t.aboutTeaser.eyebrow}</p>
          <p className="text-lg sm:text-xl leading-relaxed text-ink/85 text-balance">{t.aboutTeaser.text}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to={`/${lang}/about`} data-testid="about-teaser-more" className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-6 py-3 text-sm font-semibold transition-colors hover:border-sage hover:text-sage">
              {t.aboutTeaser.more} <ArrowRight className="w-4 h-4" />
            </Link>
            <button onClick={onBook} data-testid="booking-cta-after-about" className="inline-flex items-center gap-2 rounded-full bg-sage px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-ink">
              {t.hero.cta}
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Patients({ onBook }) {
  const { t } = useLanguage();
  return (
    <section id="patients" className="py-20 lg:py-28 bg-sand/50 scroll-mt-20" data-testid="patients-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow={t.patients.eyebrow} title={t.patients.title} />
        <Reveal delay={0.1}>
          <p className="mt-6 text-base sm:text-lg text-muted2 max-w-3xl leading-relaxed">{t.patients.intro}</p>
        </Reveal>
        <div className="mt-14 grid lg:grid-cols-2 gap-12">
          <Reveal delay={0.1}>
            <h3 className="font-serif text-2xl font-semibold mb-6">{t.patients.list_title}</h3>
            <ul className="space-y-4">
              {t.patients.list.map((item, i) => (
                <li key={i} className="flex gap-3 items-start" data-testid={`patients-item-${i}`}>
                  <span className="w-6 h-6 rounded-full bg-sage/10 text-sage flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5" /></span>
                  <span className="text-sm sm:text-base leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <h3 className="font-serif text-2xl font-semibold mb-6">{t.patients.requests_title}</h3>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {t.patients.requests.map((item, i) => (
                <li key={i} className="flex gap-2.5 items-start text-sm text-ink/80" data-testid={`patients-request-${i}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-terra mt-2 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <Reveal delay={0.2} className="mt-12 max-w-3xl border-l-2 border-terra/60 pl-6">
          <p className="text-base leading-relaxed">{t.patients.special}</p>
          <p className="text-sm text-muted2 leading-relaxed mt-3">{t.patients.special2}</p>
        </Reveal>
        <InlineCta label={t.hero.cta} onBook={onBook} />
      </div>
    </section>
  );
}

function Directions() {
  const { t } = useLanguage();
  return (
    <section id="directions" className="py-20 lg:py-28 scroll-mt-20" data-testid="directions-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow={t.directions.eyebrow} title={t.directions.title} />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.directions.items.map((d, i) => {
            const Icon = DIRECTION_ICONS[i];
            return (
              <Reveal key={i} delay={(i % 3) * 0.08} className={i === 6 ? "sm:col-span-2 lg:col-span-1" : ""}>
                <div className="group bg-white rounded-2xl border border-line p-8 h-full transition-all hover:shadow-xl hover:-translate-y-1 hover:border-terra/40" data-testid={`direction-card-${i}`}>
                  <span className="w-12 h-12 rounded-2xl bg-sage/10 text-sage flex items-center justify-center mb-5 transition-colors group-hover:bg-terra group-hover:text-white">
                    <Icon className="w-5 h-5" />
                  </span>
                  <h3 className="font-serif text-xl sm:text-2xl font-semibold mb-3">{d.name}</h3>
                  <p className="text-sm text-muted2 leading-relaxed">{d.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Approach({ onBook }) {
  const { t } = useLanguage();
  return (
    <section id="approach" className="py-20 lg:py-28 bg-sand/50 scroll-mt-20" data-testid="approach-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow={t.approach.eyebrow} title={t.approach.title} />
        <Reveal delay={0.1}>
          <p className="mt-6 text-base sm:text-lg text-muted2 max-w-3xl leading-relaxed">{t.approach.text}</p>
        </Reveal>
        <div className="mt-14 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="space-y-8">
              {t.approach.principles.map((p, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="flex gap-6 group" data-testid={`approach-principle-${i + 1}`}>
                    <span className="font-serif text-4xl lg:text-5xl text-terra/40 leading-none transition-colors group-hover:text-terra">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h3 className="font-serif text-xl sm:text-2xl font-semibold mb-2">{p.title}</h3>
                      <p className="text-muted2 leading-relaxed text-sm sm:text-base">{p.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <InlineCta label={t.hero.cta} onBook={onBook} />
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="bg-white rounded-3xl border border-line p-8 sm:p-10 sticky top-24" data-testid="philosophy-block">
                <h3 className="font-serif text-2xl font-semibold mb-4">{t.approach.philosophy_title}</h3>
                <p className="text-sm text-muted2 leading-relaxed mb-6">{t.approach.philosophy_text}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {t.approach.philosophy_factors.map((f, i) => (
                    <span key={i} className="rounded-full bg-sand px-3 py-1.5 text-xs font-medium">{f}</span>
                  ))}
                </div>
                <ul className="space-y-3">
                  {t.approach.philosophy_principles.map((p, i) => (
                    <li key={i} className="flex gap-3 items-start text-sm">
                      <Check className="w-4 h-4 text-sage mt-0.5 shrink-0" /> {p}
                    </li>
                  ))}
                </ul>
                <InlineCta label={t.hero.cta} onBook={onBook} />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function Process({ onBook }) {
  const { t } = useLanguage();
  return (
    <section id="process" className="py-20 lg:py-28 scroll-mt-20" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <SectionHead eyebrow={t.process.eyebrow} title={t.process.title} />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base sm:text-lg text-muted2 leading-relaxed">{t.process.idea}</p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="bg-sand/70 rounded-3xl p-3 shadow-xl">
              <img src={IMAGES.process} alt="Tatiana Nakryzhskaya, MD, PhD, CFMP" className="rounded-2xl w-full h-auto" data-testid="process-photo" />
            </div>
          </Reveal>
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {t.process.steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="bg-white rounded-2xl border border-line p-8 h-full flex flex-col" data-testid={`process-step-${i + 1}`}>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-terra mb-2">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="font-serif text-2xl font-semibold">{s.name}</h3>
                <p className="text-sm font-semibold text-sage mt-1 mb-5">{s.time}</p>
                <ul className="space-y-2.5 flex-1">
                  {s.points.map((pt, j) => (
                    <li key={j} className="flex gap-2.5 items-start text-sm text-ink/80">
                      <span className="w-1.5 h-1.5 rounded-full bg-terra mt-2 shrink-0" />
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 text-sm text-muted2 max-w-3xl">{t.process.after}</p>
        </Reveal>
        <InlineCta label={t.process.cta} onBook={onBook} />
      </div>
    </section>
  );
}

function Diagnostics({ onBook }) {
  const { t } = useLanguage();
  return (
    <section id="diagnostics" className="py-20 lg:py-28 bg-sand/50 scroll-mt-20" data-testid="diagnostics-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead eyebrow={t.diagnostics.eyebrow} title={t.diagnostics.title} />
        <Reveal delay={0.1}>
          <p className="mt-6 text-base sm:text-lg text-muted2 max-w-3xl leading-relaxed">{t.diagnostics.intro}</p>
          <div className="flex flex-wrap gap-2 mt-5">
            {t.diagnostics.intro_list.map((item, i) => (
              <span key={i} className="rounded-full bg-white border border-line px-3.5 py-1.5 text-xs font-medium">{item}</span>
            ))}
          </div>
          <p className="mt-6 max-w-3xl border-l-2 border-terra/60 pl-5 text-sm sm:text-base font-medium">{t.diagnostics.principle}</p>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 gap-6">
          {t.diagnostics.groups.map((g, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <div className="bg-white rounded-2xl border border-line p-8 h-full" data-testid={`diagnostics-group-${i + 1}`}>
                <h3 className="font-serif text-xl sm:text-2xl font-semibold mb-5">{g.name}</h3>
                <ul className="space-y-2.5">
                  {g.items.map((item, j) => (
                    <li key={j} className="flex gap-2.5 items-start text-sm text-ink/80">
                      <span className="w-1.5 h-1.5 rounded-full bg-sage mt-2 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1} className="mt-14">
          <h3 className="font-serif text-2xl sm:text-3xl font-semibold mb-6">{t.diagnostics.lab_title}</h3>
          <div className="flex flex-wrap gap-2 mb-6">
            {t.diagnostics.lab_items.map((item, i) => (
              <span key={i} className="rounded-full bg-sage/10 text-sage px-3.5 py-1.5 text-xs font-medium" data-testid={`lab-item-${i}`}>{item}</span>
            ))}
          </div>
          <p className="text-sm text-muted2 max-w-3xl mb-8">{t.diagnostics.lab_principle}</p>
          <p className="font-serif text-3xl sm:text-4xl text-sage italic" data-testid="diagnostics-phrase">{t.diagnostics.phrase}</p>
        </Reveal>
        <InlineCta label={t.hero.cta} onBook={onBook} />
      </div>
    </section>
  );
}

function Faq({ onBook }) {
  const { t } = useLanguage();
  return (
    <section id="faq" className="py-20 lg:py-28 scroll-mt-20" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <SectionHead eyebrow={t.faq.eyebrow} title={t.faq.title} />
        <Reveal delay={0.1} className="mt-10">
          <Accordion type="single" collapsible data-testid="faq-accordion">
            {t.faq.items.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-line">
                <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left font-serif text-lg sm:text-xl font-semibold hover:text-terra hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm sm:text-base text-muted2 leading-relaxed" data-testid={`faq-answer-${i}`}>
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
        <InlineCta label={t.hero.cta} onBook={onBook} />
      </div>
    </section>
  );
}

function FinalCta({ onBook }) {
  const { t } = useLanguage();
  return (
    <section className="relative py-20 lg:py-28 bg-sage text-cream overflow-hidden grain" data-testid="final-cta">
      <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-balance">{t.finalCta.title}</h2>
          <p className="mt-6 text-cream/75 max-w-2xl mx-auto leading-relaxed">{t.finalCta.text}</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <button onClick={onBook} data-testid="booking-cta-final" className="inline-flex items-center gap-2 rounded-full bg-terra px-8 py-4 text-sm font-semibold text-white transition-all hover:bg-white hover:text-sage-dark hover:shadow-2xl">
              {t.finalCta.cta} <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => scrollToId("process")} data-testid="final-cta-secondary" className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-8 py-4 text-sm font-semibold transition-colors hover:bg-cream/10">
              {t.finalCta.cta2}
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function HomePage() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const open = () => setBookingOpen(true);
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const timer = setTimeout(() => scrollToId(id), 400);
      return () => clearTimeout(timer);
    }
  }, [hash]);
  return (
    <main data-testid="home-page">
      <Seo page="home" withFaq />
      <Hero onBook={open} />
      <AboutTeaser onBook={open} />
      <Patients onBook={open} />
      <Directions />
      <Approach onBook={open} />
      <Process onBook={open} />
      <Diagnostics onBook={open} />
      <Faq onBook={open} />
      <FinalCta onBook={open} />
      <BookingModal open={bookingOpen} onOpenChange={setBookingOpen} />
    </main>
  );
}
